import { createContext, useContext } from "react";

import { AuthCallbackPath } from "./callback-paths";

/**
 * The Django route segment each connector's OAuth-start redirect lives at, under
 * `/:customerSlug/auth/...` (see `thirdpartyauth/urls.py`). Navigation only - never `/api`-prefixed,
 * since the browser follows it directly rather than the client fetching it.
 *
 * `shopify2` is deliberately absent: its connect flow goes through `connectPostPath` (a POST, not a
 * redirect - see `CONNECT_POST_ROUTES`), and its *reconnect* redirect needs the shop domain as a URL
 * segment, which only the server can read from a KMS-decrypted connection secret. That one path stays
 * server-computed (`ConnectionPropertiesSchema.reconnectPath`) rather than built here.
 */
const AUTH_START_ROUTES: Record<string, string> = {
  amazonrutter: "auth/amazon/start/",
  basiq: "auth/basiq/start",
  bigcommercerutter: "auth/bigcommerce/start/",
  codat_accounting: "auth/codat-accounting/start",
  facebook2: "auth/facebook2/start/",
  ga: "auth/ga/start/",
  google_ads2: "auth/google-ads2/start/",
  netsuite: "auth/netsuite/start",
  quickbooks: "auth/quickbooks/start",
  "shopify-appstore": "auth/shopify/app-store/open",
  stripe2: "auth/stripe2/start/",
  tiktok: "auth/tiktok/start",
  tiktok_admin: "auth/tiktok-admin/start",
  truelayer: "auth/truelayer/start",
  woocommerce2: "auth/woocommerce2/start/",
  xero: "auth/xero/start",
};

/**
 * The connect-page redirect for a connector with no user-input step: `window.location.href` target,
 * built client-side rather than read off the API response. Unprefixed - the browser navigates to a
 * real Django route directly, so it must not go through `/api`.
 *
 * `nextPath` becomes the `next_path` query param the auth-start view redirects back to on success.
 */
export const authStartPath = (
  customerSlug: string,
  connectorSlug: string,
  nextPath: AuthCallbackPath
): string | null => {
  const route = AUTH_START_ROUTES[connectorSlug];
  if (!route) {
    return null;
  }
  return `/${customerSlug}/${route}?${new URLSearchParams({ next_path: nextPath }).toString()}`;
};

/**
 * The `getting-started-connect-<slug>` POST target for a connector needing user input (store name,
 * access token, ...) before OAuth (see `CONNECTORS_WITH_POST_REQUIREMENTS` in `core/connectors/urls.py`).
 * `/api`-prefixed - unlike `authStartPath`, this is a real API call the client makes, not a navigation.
 *
 * `nextPath` becomes the `next_path` query param the view reads to decide where the *eventual*
 * OAuth-complete redirect lands (`get_auth_return_path_from_request` in `core/connectors/urls.py`).
 * Without it, the server falls back to the referer header, then to a hardcoded settings-connections
 * default - silently sending callers like the funding-application AIR flow back to the wrong page.
 */
const CONNECT_POST_ROUTES: Record<string, string> = {
  amazonrutter: "getting-started/connect-amazonrutter",
  bigcommercerutter: "getting-started/connect-bigcommercerutter",
  magento2: "getting-started/connect-magento2",
  mockify: "getting-started/connect-mockify",
  shopify2: "getting-started/connect-shopify2",
  woocommerce2: "getting-started/connect-woocommerce2",
};

export const connectPostPath = (
  customerSlug: string,
  connectorSlug: string,
  nextPath: AuthCallbackPath
): string | null => {
  const route = CONNECT_POST_ROUTES[connectorSlug];
  if (!route) {
    return null;
  }
  return `/api/${customerSlug}/${route}?${new URLSearchParams({ next_path: nextPath }).toString()}`;
};

/**
 * The reconnect-redirect variant of `authStartPath`: the identical route, with `connection_id`/
 * `reconnect` added to the query string - there is no separate reconnect URL name server-side.
 *
 * Not valid for `shopify2` (absent from `AUTH_START_ROUTES` - see its comment there); callers must
 * keep reading `connection.reconnectPath` off the API response for that one connector instead of
 * calling this.
 */
export const reconnectStartPath = (
  customerSlug: string,
  connectorSlug: string,
  connectionId: number,
  nextPath: AuthCallbackPath
): string | null => {
  const route = AUTH_START_ROUTES[connectorSlug];
  if (!route) {
    return null;
  }
  const params = new URLSearchParams({
    connection_id: String(connectionId),
    reconnect: "True",
    next_path: nextPath,
  });
  return `/${customerSlug}/${route}?${params.toString()}`;
};

/**
 * The reconnect-POST variant of `connectPostPath`. Unlike `reconnectStartPath`, this covers
 * `shopify2` too: the POST target takes no URL kwargs server-side, so it's a plain query-string
 * variant of the same route for every connector in `CONNECT_POST_ROUTES`.
 */
export const reconnectPostPath = (
  customerSlug: string,
  connectorSlug: string,
  connectionId: number,
  nextPath: AuthCallbackPath
): string | null => {
  const route = CONNECT_POST_ROUTES[connectorSlug];
  if (!route) {
    return null;
  }
  const params = new URLSearchParams({
    connection_id: String(connectionId),
    reconnect: "True",
    next_path: nextPath,
  });
  return `/api/${customerSlug}/${route}?${params.toString()}`;
};

export interface ConnectionFlowsConfig {
  /** `:customerSlug`-templated connectors endpoint (properties + connections). */
  connectorsApiUrl: string;
  /** `:customerSlug`-templated complete-connection endpoint (post-OAuth account/property selection). */
  completeConnectionApiUrl: string;
  /** `:customerSlug`-templated endpoint to submit (POST) a completed connection. */
  completeConnectionSubmitApiUrl: string;
  /** `:customerSlug`-templated endpoint to submit (POST) a cancelled connection. */
  cancelConnectionSubmitApiUrl: string;
  /** Callback path a connector returns to after OAuth when a flow doesn't pass its own. */
  defaultCallbackPath: AuthCallbackPath;
  /**
   * Whether this surface supports self-serve reconnection of a broken connection. When `false`,
   * `ConnectionsList` shows every connection as connected and never renders a Reconnect
   * action/form.
   */
  supportsReconnection: boolean;
}

/**
 * Defaults target the funding-application onboarding surface so existing usages keep working
 * without a provider. Other surfaces (e.g. the profile settings "Manage connections" page) wrap
 * their flows in `<ConnectionFlowsProvider>` with their own endpoints, callback path, and
 * reconnection behavior.
 *
 * Keep these URLs in sync with `FUNDING_APPLICATION_CONNECTORS_API` /
 * `FUNDING_APPLICATION_COMPLETE_CONNECTION_API` in funding-application, and with the
 * getting-started complete/cancel-connection endpoints the submit URLs point at.
 *
 * `supportsReconnection` is `false` here because onboarding doesn't yet support the reconnect UX.
 */
const DEFAULT_CONFIG: ConnectionFlowsConfig = {
  connectorsApiUrl: "/api/:customerSlug/funding/api/connectors/",
  completeConnectionApiUrl: "/api/:customerSlug/funding/api/connectors/complete-connection",
  completeConnectionSubmitApiUrl: "/api/:customerSlug/getting-started/complete-connection",
  cancelConnectionSubmitApiUrl: "/api/:customerSlug/getting-started/cancel-connection",
  defaultCallbackPath: AuthCallbackPath.FUNDING_APPLICATION_ADD_CONNECTION,
  supportsReconnection: false,
};

export const ConnectionFlowsConfigContext = createContext<ConnectionFlowsConfig>(DEFAULT_CONFIG);

export const useConnectionFlowsConfig = (): ConnectionFlowsConfig => useContext(ConnectionFlowsConfigContext);
